/**
 * Where a wood goes — the siting arithmetic behind ENHANCED TERRAIN's
 * decoration, split out of `terrain-decoration.ts` at the 400-line ceiling and
 * at the seam that was already there.
 *
 * Nothing here draws. This is what a copse and a wood *are* (how many trees,
 * over what radius), which landcover colours may hold one, and — the part that
 * has cost this port a user report — how a binding cap of `MAX_WOOD_SITES` is
 * spread across a region's green triangles. The renderer half, which turns
 * these sites into `InstancedMesh`es, is `terrain-decoration.ts`; the module doc
 * there carries the provenance argument for reading green landcover as
 * woodland, and what in all of this is measured against what is invented.
 *
 * Two consumers read the same list and must agree site for site — the trees and
 * G20's canopy ground paint (`wood-ground-paint.ts`) — which is why everything
 * here is deterministic and seeded from a patch's own index rather than from
 * anything the caller happens to be holding.
 */

/** Trees in a copse placed by the original — it draws three crowns, so a copse is a small stand. */
export const COPSE_TREES = 9;
/** Radius a copse's trees are scattered over, metres — the shipped object's own 47 m extent, halved. */
export const COPSE_RADIUS_M = 23;

/**
 * One wood per this many square metres of green landcover.
 *
 * Chosen against a *coverage fraction*, not picked. A wood here is a
 * `WOOD_RADIUS_M` disc — 0.053 km² — so one per 0.35 km² asks for roughly **15%
 * of the green landcover under trees**, which is the low end of what French
 * farmland actually is. The first pass used one per 2.2 km², i.e. 2.4%, and the
 * result was a feature nobody could find: a low pass over Verdun crossed no
 * wood at all, and 28 frames of census could not tell the toggle's two states
 * apart.
 *
 * **What is asked for is not what is drawn, and this comment used to claim it
 * was.** `MAX_WOOD_SITES` truncates the ask to 400 in every region, so the
 * closure actually delivered is **2.5% on Verdun, 3.2% London, 8.8% Paris,
 * 1.8% Somme, 2.2% Dunkirk** (measured 2026-09-01 against the shipped bake) —
 * i.e. four of the five regions sit within a whisker of the 2.4% this constant
 * was retuned to escape. The retune was undone by a second constant in the same
 * file that nobody re-derived it against.
 *
 * Raising the cap is the open question and it is a **frame-budget** one, not a
 * taste one: honouring the ask means 685-3,349 sites a region at 26 trees each,
 * i.e. up to ~87,000 trees against today's ~10,400. It is deliberately not
 * changed here — the 2026-09-01 lane fixed *where* the 400 go (`siteQuota`) and
 * left *how many* to a lane that can measure the cost on a weak GPU.
 */
export const WOOD_AREA_PER_SITE_M2 = 3.5e5;
/** Trees in a derived wood, and the radius they cover. */
export const WOOD_TREES = 26;
export const WOOD_RADIUS_M = 130;
/**
 * Ceiling on derived woods per region, so a huge patchwork cannot run away with
 * the frame budget. At 400 sites Verdun draws ~10 000 trees — still four draw
 * calls, because the cost of a site is instances rather than a batch.
 *
 * **The ceiling binds hard, and `woodSitesFrom` has to spend it evenly.**
 * `WOOD_AREA_PER_SITE_M2` asks each region for far more sites than this:
 * measured over the five shipped regions the ask is 685 (Paris) to 3 349
 * (Somme), so the cap keeps only **12–21 %** of them. That is not a rounding
 * loss, it is the whole allocation — which is why *how* the survivors are
 * chosen decides whether the feature exists for a pilot at all.
 */
export const MAX_WOOD_SITES = 400;

/** Green landcover entries — the ones the game itself paints foliage with. See the module doc. */
export const FOLIAGE_GROUND_COLOURS: readonly number[] = [0x616d10, 0x516534, 0x556134, 0x4d5928];

/**
 * Deterministic integer hash → [0,1).
 *
 * Exported because both halves need the *same* one: a wood's position comes from
 * here and the scatter of trees inside it comes from `terrain-decoration.ts`,
 * and a second hash would make the two lists drift apart on a machine that
 * rounded differently.
 */
export function hash01(a: number, b: number): number {
  let h = Math.imul(a, 0x27d4eb2d) ^ Math.imul(b, 0x165667b1);
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
  h ^= h >>> 13;
  return (h >>> 0) / 4294967296;
}

export interface TreeSite {
  readonly x: number;
  readonly z: number;
  readonly count: number;
  readonly radiusM: number;
}

/** A landcover triangle, world space, already draped — what wood sites are derived from. */
export interface FoliagePatch {
  readonly ax: number;
  readonly az: number;
  readonly bx: number;
  readonly bz: number;
  readonly cx: number;
  readonly cz: number;
}

/** Twice the signed area of a landcover triangle's XZ footprint, unsigned. */
function patchArea(p: FoliagePatch): number {
  return Math.abs((p.bx - p.ax) * (p.cz - p.az) - (p.cx - p.ax) * (p.bz - p.az)) / 2;
}

/**
 * How many of `MAX_WOOD_SITES` each patch gets — **every patch's share of the
 * region's green landcover**, not "whoever is first in record order until the
 * cap runs out".
 *
 * That distinction is the whole of the 2026-09-01 "I don't see trees" report,
 * and it was invisible to every census that had been run on this code, because
 * the *totals* were always right: 400 woods derived, 400 woods painted, in
 * every region. What no count asked was **where**. The greedy fill walked
 * `patches` in `.WLD` record order and stopped at the cap, so the four largest
 * early triangles took all 400 and the remaining eleven — most of the map —
 * got none. Measured on the shipped bake, that put every one of Verdun's woods
 * inside a **19 x 13 km box 50 km from the spawn**, and Dunkirk's 50-86 km west
 * of its own: a patrol from either field crossed *no wood at all*, which is
 * exactly what a pilot reported.
 *
 * The allocation is largest-remainder over the patches' area shares, which is
 * the standard way to hand out an integer quota without the rounding all
 * landing on one end. Ties break on patch index so the answer is the same on
 * every machine and every run — `woodSitesFrom` feeds both the trees and G20's
 * canopy ground paint, and the two must agree site for site.
 */
function siteQuota(areas: readonly number[]): number[] {
  const wanted = areas.map((area) => Math.floor(area / WOOD_AREA_PER_SITE_M2));
  const total = wanted.reduce((sum, n) => sum + n, 0);
  if (total <= MAX_WOOD_SITES) return wanted;

  const share = MAX_WOOD_SITES / total;
  const exact = wanted.map((n) => n * share);
  const quota = exact.map((value) => Math.floor(value));
  let spare = MAX_WOOD_SITES - quota.reduce((sum, n) => sum + n, 0);
  // The leftover units go to the largest fractional parts. A patch that wants
  // nothing at all stays at nothing — a spare unit must never invent a wood in
  // a triangle too small to hold one.
  const order = exact
    .map((value, index) => ({ index, remainder: value - Math.floor(value) }))
    .filter(({ index }) => wanted[index]! > 0)
    .sort((a, b) => b.remainder - a.remainder || a.index - b.index);
  for (const { index } of order) {
    if (spare <= 0) break;
    quota[index] = (quota[index] ?? 0) + 1;
    spare -= 1;
  }
  return quota;
}

/**
 * Wood sites inside the green landcover polygons, by area, capped — and spread
 * across every patch that earns one (`siteQuota`).
 */
export function woodSitesFrom(patches: readonly FoliagePatch[]): TreeSite[] {
  const sites: TreeSite[] = [];
  const quota = siteQuota(patches.map(patchArea));
  patches.forEach((p, pi) => {
    for (let i = 0; i < quota[pi]!; i++) {
      // Uniform barycentric sample, so sites land inside the real polygon.
      // Seeded from the patch index and the site's ordinal *within that patch*,
      // so a patch's sites do not move when another patch's quota changes.
      let u = hash01(pi * 31 + i, 7);
      let v = hash01(pi * 31 + i, 11);
      if (u + v > 1) {
        u = 1 - u;
        v = 1 - v;
      }
      sites.push({
        x: p.ax + u * (p.bx - p.ax) + v * (p.cx - p.ax),
        z: p.az + u * (p.bz - p.az) + v * (p.cz - p.az),
        count: WOOD_TREES,
        radiusM: WOOD_RADIUS_M,
      });
    }
  });
  return sites;
}
